import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import Modal from '@/components/Modal'
import SubmitOverlay from '@/components/SubmitOverlay'
import { Button } from '@/components/ui'
import { linkDeliveryToRun, invalidateAfterLink } from '@/lib/linkDeliveryToRun'
import { useProductionFlow } from '@/lib/useProductionFlow'
import { useFlowManifest } from '@/hooks/useCommodityTemplates'
import clsx from 'clsx'
import { supabase } from '@/lib/supabase'
import { readState } from '@/lib/readState'
import { openRunsForDelivery, type DeliveryForRuns } from '@/lib/openRunsForDelivery'
import { useCanOpenModule } from '@/hooks/useCanOpenModule'
import { useNavigateWithReturn, useReturnHereState } from '@/lib/returnTo'

type RunRowProps = {
  run: { run_id: string; run_number: string; status: string }
  canAdd: boolean; canRuns: boolean; picked: boolean
  onPick: () => void; onOpen: () => void
}

/** One open run: pick-and-add when allowed, else a link to the run, else plain text. */
function RunRow({ run: r, canAdd, canRuns, picked, onPick, onOpen }: RunRowProps) {
  const label = r.status === 'ACTIVE' ? 'Tipping open' : 'Tipping complete'
  const text = (
    <>
      <span className="font-semibold text-fg">{r.run_number}</span>
      <span className="text-caption text-fg-muted">{label}</span>
    </>
  )
  if (canAdd && r.status === 'ACTIVE') return (
    <div className="flex items-stretch gap-2">
      <button type="button" role="radio" aria-checked={picked} onClick={onPick}
        className={clsx('flex-1 min-h-[44px] flex items-center justify-between gap-2 rounded-lg border px-3 text-left',
          picked ? 'border-accent bg-raised' : 'border-line bg-surface hover:bg-raised')}>
        {text}
      </button>
      <Button variant="ghost" size="sm" className="min-h-[44px]" onClick={onOpen}>Open</Button>
    </div>
  )
  return canRuns ? (
    <button type="button" onClick={onOpen}
      className="w-full min-h-[44px] flex items-center justify-between gap-2 rounded-lg border border-line bg-surface px-3 text-left hover:bg-raised">
      {text}
    </button>
  ) : (
    <div className="min-h-[44px] flex items-center justify-between gap-2 rounded-lg border border-line px-3">{text}</div>
  )
}

/** Loading, unread and empty are three different answers: never "no runs" from a failed read. */
function ReadNotice({ state, empty }: { state: ReturnType<typeof readState>; empty: boolean }) {
  if (state === 'loading') return <p className="text-body text-fg-muted">Reading the open runs…</p>
  if (state === 'unread') return (
    <p role="alert" className="text-body text-warning">
      The open runs could not be read. This is not an empty list. Close this and try again.
    </p>
  )
  if (state === 'usable' && empty) return <p className="text-body text-fg-secondary">No open run for this grower and variety.</p>
  return null
}

function RunsFooter({ canRuns, canAdd, hasRuns }: { canRuns: boolean; canAdd: boolean; hasRuns: boolean }) {
  const returnState = useReturnHereState()
  if (!canRuns) return null
  return (
    <>
      <p className="text-caption text-fg-muted pt-1">
        No right run? <Link to="/runs" state={returnState} className="text-link underline">Start one on Runs</Link>
      </p>
      {hasRuns && !canAdd && <p className="text-caption text-fg-muted">Open the run and tap Add another delivery.</p>}
    </>
  )
}

/** The run ticked for "Add to run": a lone addable run is pre-picked. */
function usePickedRun(addable: { run_id: string }[]) {
  const [picked, setPicked] = useState<string | null>(null)
  const chosen = picked && addable.some(r => r.run_id === picked)
    ? picked : addable.length === 1 ? addable[0].run_id : null
  return { chosen, setPicked }
}

function useLinkMutation(deliveryId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (runId: string) => linkDeliveryToRun(supabase, runId, deliveryId),
    onSuccess: (_r, runId) => invalidateAfterLink(qc, runId, deliveryId),
  })
}

/** The runs an unallocated delivery can go to. Each row opens the run (Back
 *  returns here). A failed or pending read never says "no runs". */
export function RunsForDeliverySheet({ delivery, onClose }: {
  delivery: DeliveryForRuns & { delivery_id: string; bins_remaining?: number }
  onClose: () => void
}) {
  const q = useQuery({
    queryKey: ['open-runs-for-delivery', delivery.delivery_id],
    queryFn: () => openRunsForDelivery(supabase, delivery),
  })
  const canRuns = useCanOpenModule('runs')
  const go = useNavigateWithReturn()
  // Same gate as Runs' "Add another delivery": the Runs page is open to this
  // reader. Berry runs are fed by cooling batches, never by linked deliveries.
  const manifest = useFlowManifest(useProductionFlow())
  const canAdd = canRuns === true && !manifest.usesForceCooling
  const state = readState(q)
  const runs = q.data ?? []
  // Only a run still tipping (ACTIVE) takes a delivery, as on Runs.
  const addable = runs.filter(r => r.status === 'ACTIVE')
  const { chosen, setPicked } = usePickedRun(addable)
  const link = useLinkMutation(delivery.delivery_id)
  return (
    <Modal title="Put on a run" subtitle={`${delivery.grower} · ${delivery.variety}`} onClose={onClose} noFooter size="sm">
      <SubmitOverlay
        isPending={link.isPending} isSuccess={link.isSuccess} isError={link.isError} error={link.error}
        successLabel={link.data?.alreadyLinked ? 'Already on this run' : 'Delivery added to run'}
        onDismiss={() => { const ok = link.isSuccess; link.reset(); if (ok) onClose() }}
      />
      <div className="space-y-2" role="radiogroup" aria-label="Open runs for this delivery">
        <ReadNotice state={state} empty={runs.length === 0} />
        {state === 'usable' && runs.map(r => (
          <RunRow key={r.run_id} run={r} canAdd={canAdd} canRuns={canRuns === true}
            picked={chosen === r.run_id} onPick={() => setPicked(r.run_id)}
            onOpen={() => go(`/runs/${r.run_id}`)} />
        ))}
        {state === 'usable' && canAdd && addable.length > 0 && (
          <Button variant="primary" className="w-full min-h-[44px]" disabled={!chosen || link.isPending}
            onClick={() => chosen && link.mutate(chosen)}>
            Add to run
          </Button>
        )}
        {state === 'usable' && <RunsFooter canRuns={canRuns === true} canAdd={canAdd} hasRuns={runs.length > 0} />}
      </div>
    </Modal>
  )
}
